<template>
  <lb-dialog
    :value="value"
    :heading="title"
    :width="dialogWidth"
    :loading="loading"
    content-class="upsi-publish-dialog"
    @input="onInput"
  >
    <template #body>
      <div ref="formBody" class="upsi-publish-dialog__body" data-testid="upsi-publish-dialog">
        <p class="upsi-publish-dialog__lead">{{ bannerMessage }}</p>
        <UpsiSummaryList :rows="summaryRows" />
        <UpsiActivityHistory
          :history="historyRows"
          :filing-reference="storedFilingReference"
          test-id-prefix="upsi-publish-dialog"
        />

        <div class="upsi-publish-dialog__field">
          <label
            class="upsi-publish-dialog__label"
            for="upsi-publish-dialog__filing-reference-input"
          >
            {{ filingLabel }}
            <span class="upsi-publish-dialog__required" aria-hidden="true">*</span>
          </label>
          <div class="upsi-publish-dialog__control upsi-publish-dialog__control--input">
            <input
              id="upsi-publish-dialog__filing-reference-input"
              class="upsi-publish-dialog__input upsi-publish-dialog__input--preserve-case"
              :class="{
                'upsi-publish-dialog__input--error': !!filingError,
                'upsi-publish-dialog__input--clearable': showFilingClear,
              }"
              type="text"
              :value="filingReference"
              :placeholder="filingPlaceholder"
              :maxlength="filingMax"
              :disabled="loading"
              data-testid="upsi-publish-dialog__filing-reference-input"
              @input="onFilingInput"
            >
            <button
              v-if="showFilingClear"
              type="button"
              class="upsi-publish-dialog__clear"
              aria-label="Clear filing reference"
              data-testid="upsi-publish-dialog__filing-reference-clear"
              @mousedown.prevent
              @click.stop="clearFilingReference"
            >
              <v-icon class="upsi-publish-dialog__clear-icon" aria-hidden="true">mdi-close</v-icon>
            </button>
          </div>
          <p
            v-if="filingError"
            class="upsi-publish-dialog__error"
            data-testid="upsi-publish-dialog__filing-reference-error"
          >
            {{ filingError }}
          </p>
        </div>

        <div class="upsi-publish-dialog__field">
          <label
            class="upsi-publish-dialog__label"
            for="upsi-publish-dialog__remarks-input"
          >
            {{ remarksLabel }}
          </label>
          <div class="upsi-publish-dialog__control upsi-publish-dialog__control--textarea">
            <textarea
              id="upsi-publish-dialog__remarks-input"
              class="upsi-publish-dialog__input upsi-publish-dialog__textarea"
              :class="{
                'upsi-publish-dialog__input--error': !!remarksError,
                'upsi-publish-dialog__input--clearable': showRemarksClear,
              }"
              :value="remarks"
              :placeholder="remarksPlaceholder"
              :maxlength="remarksMax"
              :disabled="loading"
              data-testid="upsi-publish-dialog__remarks-input"
              @input="onRemarksInput"
            />
            <button
              v-if="showRemarksClear"
              type="button"
              class="upsi-publish-dialog__clear"
              aria-label="Clear remarks"
              data-testid="upsi-publish-dialog__remarks-clear"
              @mousedown.prevent
              @click.stop="clearRemarks"
            >
              <v-icon class="upsi-publish-dialog__clear-icon" aria-hidden="true">mdi-close</v-icon>
            </button>
          </div>
          <p
            v-if="remarksError"
            class="upsi-publish-dialog__error"
            data-testid="upsi-publish-dialog__remarks-error"
          >
            {{ remarksError }}
          </p>
        </div>
      </div>
    </template>
    <template #actions>
      <div class="upsi-publish-dialog__footer">
        <button
          type="button"
          class="upsi-publish-dialog__cancel"
          :disabled="loading"
          data-testid="upsi-publish-dialog__cancel-btn"
          @click="onCancel"
        >
          {{ cancelLabel }}
        </button>
        <button
          type="button"
          class="upsi-publish-dialog__submit"
          :disabled="loading"
          data-testid="upsi-publish-dialog__submit-btn"
          @click="onSubmit"
        >
          {{ publishLabel }}
        </button>
      </div>
    </template>
  </lb-dialog>
</template>

<script>
import './UpsiPublishDialog.scss';
import { UpsiActivityHistory, UpsiSummaryList } from '../upsi';
import { UPSI_LIMITS, UPSI_MESSAGES } from '../../../../utils/constants';
import {
  UPSI_DIALOG_WIDTH,
  UPSI_EMPTY,
  UPSI_FIELD_LABELS,
  UPSI_SUMMARY_LABELS,
  readServerError,
  scrollUpsiDialogToBottom,
  upsiStageLabel,
  upsiStateOf,
} from '../../components/upsi/upsiDialogUtils';

/** Request or republish a cycle publish request. Presentational: emits submit. */
export default {
  name: 'UpsiPublishDialog',

  components: {
    UpsiActivityHistory,
    UpsiSummaryList,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'request',
      validator: (mode) => ['request', 'republish'].includes(mode),
    },
    cycle: {
      type: Object,
      default: () => ({}),
    },
    details: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    serverErrors: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      filingReference: '',
      remarks: '',
      filingClientError: '',
      remarksClientError: '',
      filingTouched: false,
      remarksTouched: false,
      formEdited: false,
      previewIso: '',
      dialogWidth: UPSI_DIALOG_WIDTH,
      filingLabel: UPSI_FIELD_LABELS.FILING_REFERENCE,
      filingPlaceholder: UPSI_FIELD_LABELS.FILING_REFERENCE_PLACEHOLDER,
      remarksLabel: UPSI_FIELD_LABELS.REMARKS_OPTIONAL,
      remarksPlaceholder: UPSI_FIELD_LABELS.REMARKS_PLACEHOLDER,
      cancelLabel: UPSI_FIELD_LABELS.CANCEL,
      publishLabel: UPSI_FIELD_LABELS.PUBLISH,
      filingMax: UPSI_LIMITS.FILING_REFERENCE_MAX,
      remarksMax: UPSI_LIMITS.REMARKS_MAX,
    };
  },

  computed: {
    isRepublish() {
      return this.mode === 'republish';
    },
    upsiState() {
      return upsiStateOf(this.details);
    },
    title() {
      return UPSI_MESSAGES.DIALOG_TITLE;
    },
    bannerMessage() {
      return this.isRepublish ? UPSI_MESSAGES.BANNER_REPUBLISH : UPSI_MESSAGES.BANNER_REQUEST;
    },
    cycleName() {
      const fromDetails = this.details && this.details.cycle && this.details.cycle.description;
      const fromCycle = this.cycle && this.cycle.description;
      return fromDetails || fromCycle || UPSI_EMPTY;
    },
    upsiStage() {
      if (this.details && this.details.upsi_state && this.details.upsi_state.stage != null) {
        return this.details.upsi_state.stage;
      }
      if (this.cycle && this.cycle.upsi_state && this.cycle.upsi_state.stage != null) {
        return this.cycle.upsi_state.stage;
      }
      return 0;
    },
    summaryRows() {
      return [
        { label: UPSI_SUMMARY_LABELS.CYCLE, value: this.cycleName },
        { label: UPSI_SUMMARY_LABELS.UPSI_STATUS, value: upsiStageLabel(this.upsiStage) },
      ];
    },
    historyRows() {
      return this.details && Array.isArray(this.details.history) ? this.details.history : [];
    },
    storedFilingReference() {
      return this.upsiState.filing_reference || '';
    },
    showFilingClear() {
      return !this.loading && this.filingReference !== '';
    },
    showRemarksClear() {
      return !this.loading && this.remarks !== '';
    },
    filingError() {
      if (this.filingClientError) return this.filingClientError;
      if (this.filingTouched) return '';
      return readServerError(this.serverErrors, ['filingReference', 'filing_reference']);
    },
    remarksError() {
      if (this.remarksClientError) return this.remarksClientError;
      if (this.remarksTouched) return '';
      return readServerError(this.serverErrors, ['remarks', 'request_remarks']);
    },
    cycleId() {
      return this.cycle && this.cycle._id;
    },
  },

  watch: {
    value(open) {
      if (open) this.seedForm();
    },
    filingError(message) {
      if (message) this.scrollValidationIntoView();
    },
    remarksError(message) {
      if (message) this.scrollValidationIntoView();
    },
    cycleId(next, prev) {
      if (this.value && next && next !== prev) this.seedForm();
    },
    mode() {
      if (this.value) this.seedForm();
    },
    details() {
      if (this.value && this.isRepublish && !this.formEdited) {
        this.applyRepublishPrefill(this.details);
      }
    },
  },

  created() {
    if (this.value) this.seedForm();
  },

  beforeDestroy() {
    this.filingReference = '';
    this.remarks = '';
    this.filingClientError = '';
    this.remarksClientError = '';
    this.filingTouched = false;
    this.remarksTouched = false;
    this.formEdited = false;
    this.previewIso = '';
  },

  methods: {
    seedForm() {
      console.log('[UpsiPublishDialog]:', { mode: this.mode });
      this.filingClientError = '';
      this.remarksClientError = '';
      this.filingTouched = false;
      this.remarksTouched = false;
      this.formEdited = false;
      this.previewIso = new Date().toISOString();
      if (this.isRepublish) {
        this.applyRepublishPrefill(this.details);
      } else {
        this.filingReference = '';
        this.remarks = '';
      }
    },
    applyRepublishPrefill(details) {
      const state = upsiStateOf(details);
      this.filingReference = state.filing_reference || '';
      this.remarks = state.request_remarks || '';
    },
    onFilingInput(event) {
      const value = event.target.value || '';
      this.filingReference = value.slice(0, UPSI_LIMITS.FILING_REFERENCE_MAX);
      this.filingClientError = '';
      this.filingTouched = true;
      this.formEdited = true;
    },
    onRemarksInput(event) {
      const value = event.target.value || '';
      this.remarks = value.slice(0, UPSI_LIMITS.REMARKS_MAX);
      this.remarksClientError = '';
      this.remarksTouched = true;
      this.formEdited = true;
    },
    clearFilingReference() {
      this.onFilingInput({ target: { value: '' } });
    },
    clearRemarks() {
      this.onRemarksInput({ target: { value: '' } });
    },
    onSubmit() {
      const filingReference = this.filingReference.trim();
      const remarks = this.remarks.trim();
      this.filingTouched = false;
      this.remarksTouched = false;
      this.filingClientError = '';
      if (!filingReference) {
        this.filingClientError = UPSI_MESSAGES.FILING_REFERENCE_REQUIRED;
        this.scrollValidationIntoView();
        return;
      }
      this.previewIso = new Date().toISOString();
      console.log('[UpsiPublishDialog]:', { action: 'submit', mode: this.mode });
      this.$emit('submit', {
        filingReference,
        remarks,
        publicationAt: this.previewIso,
      });
    },
    onInput(open) {
      this.$emit('input', open);
      if (!open) this.$emit('cancel');
    },
    onCancel() {
      this.onInput(false);
    },
    scrollValidationIntoView() {
      this.$nextTick(() => {
        scrollUpsiDialogToBottom(this.$refs.formBody);
      });
    },
  },
};
</script>
